import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
// Shared chrome: Escape closes, and the three management screens look alike
import { AdminModal as Modal, AdminField as Field } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoPersonAddOutline,
  IoTrashOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoShieldCheckmarkOutline
} from 'react-icons/io5';

const ChurchForm = ({ id, form, set, includeAdminEmail }) => (
  <form id={id} onSubmit={form.onSubmit}>
    <Field label="Church name">
      <input type="text" value={form.values.name} onChange={set('name')} maxLength={200} required />
    </Field>
    <Field label="Description">
      <textarea value={form.values.description} onChange={set('description')} rows={2} />
    </Field>
    <Field label="Address">
      <textarea value={form.values.address} onChange={set('address')} rows={2} />
    </Field>
    <Field label="Website" hint="Include https:// or leave blank.">
      <input type="url" value={form.values.website} onChange={set('website')} />
    </Field>
    <Field label="Phone">
      <input type="tel" value={form.values.phone} onChange={set('phone')} />
    </Field>
    <Field label="Contact email">
      <input type="email" value={form.values.email} onChange={set('email')} />
    </Field>
    {includeAdminEmail && (
      <Field
        label="Administering user email"
        hint="Administering a church is separate from being a member of it, so this user is not added to the roster."
      >
        <input type="email" value={form.values.adminEmail} onChange={set('adminEmail')} required />
      </Field>
    )}
    <Field label="Maximum members" hint="Counts approved members only.">
      <input type="number" min="1" max="10000" value={form.values.maxMembers} onChange={set('maxMembers')} required />
    </Field>
    <label className="admin-modal-check">
      <input type="checkbox" checked={form.values.allowPublicJoining} onChange={set('allowPublicJoining')} />
      <span>Allow joining with the join code</span>
      <em>When off, the join code stops working entirely.</em>
    </label>
    <label className="admin-modal-check">
      <input type="checkbox" checked={form.values.isActive} onChange={set('isActive')} />
      <span>Active</span>
    </label>
  </form>
);

const useChurchForm = (initial) => {
  const [values, setValues] = useState(initial);
  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };
  return { values, set, setValues };
};

export const ChurchCreateModal = ({ onClose, onSaved }) => {
  const form = useChurchForm({
    name: '', description: '', address: '', website: '', phone: '', email: '',
    adminEmail: '', maxMembers: 1000, allowPublicJoining: true, isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);

      // The API wants the administering user's id, so resolve the email first.
      const lookup = await adminApi.getUsers({ search: form.values.adminEmail, limit: 5 });
      const admin = (lookup.data.users || []).find(
        (candidate) => candidate.email.toLowerCase() === form.values.adminEmail.trim().toLowerCase()
      );
      if (!admin) {
        setError(`No user found with the email ${form.values.adminEmail}.`);
        return;
      }

      await adminApi.createChurch({
        name: form.values.name,
        description: form.values.description || null,
        address: form.values.address || null,
        website: form.values.website || null,
        phone: form.values.phone || null,
        email: form.values.email || null,
        adminUserId: admin.id,
        maxMembers: Number(form.values.maxMembers),
        allowPublicJoining: form.values.allowPublicJoining,
        isActive: form.values.isActive
      });
      onSaved(`Created church "${form.values.name}" administered by ${admin.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this church.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New church"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="church-create-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create church'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      <ChurchForm id="church-create-form" form={{ ...form, onSubmit: handleSubmit }} set={form.set} includeAdminEmail />
    </Modal>
  );
};

export const ChurchEditModal = ({ church, onClose, onSaved }) => {
  const initial = {
    name: church.name ?? '',
    description: church.description ?? '',
    address: church.address ?? '',
    website: church.website ?? '',
    phone: church.phone ?? '',
    email: church.email ?? '',
    maxMembers: church.maxMembers ?? 1000,
    allowPublicJoining: church.allowPublicJoining !== false,
    isActive: church.isActive !== false
  };
  const form = useChurchForm(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Send only what actually changed, so a PATCH cannot clobber a field the
    // admin never touched.
    const changes = {};
    for (const key of ['name', 'description', 'address', 'website', 'phone', 'email']) {
      if (form.values[key] !== initial[key]) changes[key] = form.values[key] || null;
    }
    if (Number(form.values.maxMembers) !== initial.maxMembers) changes.maxMembers = Number(form.values.maxMembers);
    if (form.values.allowPublicJoining !== initial.allowPublicJoining) changes.allowPublicJoining = form.values.allowPublicJoining;
    if (form.values.isActive !== initial.isActive) changes.isActive = form.values.isActive;

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await adminApi.updateChurch(church.id, changes);
      onSaved(`Updated church "${form.values.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this church.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit ${church.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="church-edit-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      <ChurchForm id="church-edit-form" form={{ ...form, onSubmit: handleSubmit }} set={form.set} />
    </Modal>
  );
};

/**
 * The support tool for churches: approve or reject the pending queue, add and
 * remove members on their behalf, reassign who administers it, and repair the
 * pending counter and reader links when they have drifted.
 */
export const ChurchMembersModal = ({ church, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [detailResponse, integrityResponse] = await Promise.all([
        adminApi.getChurch(church.id),
        adminApi.getChurchIntegrity(church.id)
      ]);
      setDetail(detailResponse.data.church);
      setIntegrity(integrityResponse.data.integrity);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load this church.'));
    } finally {
      setLoading(false);
    }
  }, [church.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, describe) => {
    try {
      setBusy(true);
      setError(null);
      setNotice(null);
      const result = await action();
      setNotice(describe(result));
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'That action did not go through.'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddMember = (event) => {
    event.preventDefault();
    const email = newMemberEmail.trim();
    return run(
      () => adminApi.addChurchMember(church.id, { email, status: 'approved' }),
      (result) => {
        setNewMemberEmail('');
        const mirrored = result?.data?.readersChanged ?? 0;
        return `Added ${email}${mirrored > 0 ? `, mirroring ${mirrored} reader(s)` : ''}`;
      }
    );
  };

  const handleReassignAdmin = (event) => {
    event.preventDefault();
    const email = newAdminEmail.trim();
    return run(
      async () => {
        const lookup = await adminApi.getUsers({ search: email, limit: 5 });
        const candidate = (lookup.data.users || []).find(
          (user) => user.email.toLowerCase() === email.toLowerCase()
        );
        if (!candidate) {
          throw new Error(`No user found with the email ${email}.`);
        }
        return adminApi.updateChurch(church.id, { adminUserId: candidate.id });
      },
      () => {
        setNewAdminEmail('');
        return `${email} now administers this church`;
      }
    );
  };

  const members = detail?.members || [];
  const pending = detail?.pendingMembers || [];
  const approved = detail?.approvedMembers || [];
  const readers = detail?.readers || [];

  const statusLabel = (member) => {
    if (member.churchMembershipStatus === 'approved') return null;
    return <span className="admin-modal-sub">{member.churchMembershipStatus ?? 'no status'}</span>;
  };

  return (
    <Modal
      wide
      title={`Members of ${church.name}`}
      onClose={onClose}
      footer={<button type="button" className="admin-modal-btn" onClick={onClose}>Done</button>}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      {notice && <div className="admin-modal-notice">{notice}</div>}

      {loading ? (
        <LoadingSpinner size="small" message="Loading members…" />
      ) : detail && (
        <>
          <div className="admin-modal-meta">
            <span>Join code <code>{detail.joinCode}</code></span>
            <span>{approved.length} / {detail.maxMembers} approved</span>
            {!detail.allowPublicJoining && <span>join code disabled</span>}
            <button
              type="button"
              className="admin-modal-link"
              onClick={() => run(
                () => adminApi.regenerateChurchJoinCode(church.id),
                (result) => `New join code: ${result.data.church.joinCode}`
              )}
              disabled={busy}
            >
              Regenerate code
            </button>
          </div>

          <p className="admin-modal-hint">
            Administered by {detail.admin?.email || 'unknown'}.
          </p>

          {integrity && !integrity.isConsistent && (
            <div className="admin-modal-drift">
              <h4><IoWarningOutline /> This church needs a repair</h4>
              <ul className="admin-modal-drift-list">
                {integrity.pendingCountDrift !== 0 && (
                  <li>
                    The pending-request counter says {integrity.storedPendingCount} but there
                    {' '}{integrity.actualPendingCount === 1 ? 'is' : 'are'} actually
                    {' '}{integrity.actualPendingCount}. Leaving a church while still pending never
                    decremented it.
                  </li>
                )}
                {integrity.missingReaderLinks.length > 0 && (
                  <li>
                    {integrity.missingReaderLinks.length} reader(s) belong to a member but are not in
                    this church. Readers added after joining never inherit it.
                  </li>
                )}
                {integrity.orphanedReaderLinks.length > 0 && (
                  <li>
                    {integrity.orphanedReaderLinks.length} reader(s) still point at this church but
                    their owner is no longer a member.
                  </li>
                )}
                {integrity.membersWithUnexpectedStatus.length > 0 && (
                  <li>
                    {integrity.membersWithUnexpectedStatus.length} member(s) hold a status no member
                    should have. Repair leaves these alone, because the correct status cannot be
                    guessed — approve or remove them yourself.
                  </li>
                )}
              </ul>
              <button type="button" className="admin-modal-btn is-primary" onClick={() => run(
                () => adminApi.repairChurch(church.id),
                (result) => `Attached ${result.data.attached}, released ${result.data.released}, pending counter corrected by ${result.data.pendingCountCorrectedBy}`
              )} disabled={busy}>
                Repair church
              </button>
            </div>
          )}

          {integrity?.isConsistent && (
            <p className="admin-modal-ok">
              <IoCheckmarkCircleOutline /> Counter and reader links agree.
            </p>
          )}

          {pending.length > 0 && (
            <>
              <h4 className="admin-modal-subhead">Waiting for approval ({pending.length})</h4>
              <ul className="admin-modal-list">
                {pending.map((member) => (
                  <li key={member.id}>
                    <div className="admin-modal-list-main">
                      <strong>{member.name}</strong>
                      <span className="admin-modal-sub">{member.email}</span>
                    </div>
                    <div className="admin-modal-list-actions">
                      <button type="button" onClick={() => run(
                        () => adminApi.setChurchMemberStatus(church.id, member.id, 'approved'),
                        () => `Approved ${member.email}`
                      )} disabled={busy}>
                        <IoCheckmarkCircleOutline /> Approve
                      </button>
                      <button type="button" className="is-danger" onClick={() => run(
                        () => adminApi.setChurchMemberStatus(church.id, member.id, 'rejected'),
                        () => `Rejected ${member.email}`
                      )} disabled={busy}>
                        <IoCloseCircleOutline /> Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          <form className="admin-modal-add" onSubmit={handleAddMember}>
            <input
              type="email"
              value={newMemberEmail}
              onChange={(event) => setNewMemberEmail(event.target.value)}
              placeholder="Add an approved member by email"
              required
            />
            <button type="submit" className="admin-modal-btn is-primary" disabled={busy}>
              <IoPersonAddOutline /> Add
            </button>
          </form>
          <p className="admin-modal-hint">
            Adding a member points their readers at this church too. A user belongs to one church at
            a time, so someone already in another church has to be removed from it first.
          </p>

          <h4 className="admin-modal-subhead">Members ({members.length})</h4>
          <ul className="admin-modal-list">
            {members.map((member) => (
              <li key={member.id}>
                <div className="admin-modal-list-main">
                  <strong>{member.name}</strong>
                  {member.id === detail.adminUserId && (
                    <span className="admin-modal-owner"><IoShieldCheckmarkOutline /> Administers</span>
                  )}
                  <span className="admin-modal-sub">{member.email}</span>
                  {statusLabel(member)}
                </div>
                <div className="admin-modal-list-actions">
                  <button type="button" className="is-danger" onClick={() => run(
                    () => adminApi.removeChurchMember(church.id, member.id),
                    (result) => `Removed ${member.email}, releasing ${result.data.readersChanged} reader(s)`
                  )} disabled={busy}>
                    <IoTrashOutline /> Remove
                  </button>
                </div>
              </li>
            ))}
            {members.length === 0 && <li className="admin-modal-empty">No members.</li>}
          </ul>

          <h4 className="admin-modal-subhead">Readers in this church ({readers.length})</h4>
          <ul className="admin-modal-list">
            {readers.slice(0, 25).map((reader) => (
              <li key={reader.id}>
                <div className="admin-modal-list-main">
                  <strong>{reader.name}</strong>
                  {!reader.isActive && <span className="admin-modal-sub">inactive</span>}
                  <span className="admin-modal-sub">owned by {reader.user?.email || 'unknown'}</span>
                </div>
              </li>
            ))}
            {readers.length === 0 && <li className="admin-modal-empty">No readers.</li>}
            {readers.length > 25 && (
              <li className="admin-modal-empty">…and {readers.length - 25} more.</li>
            )}
          </ul>

          <h4 className="admin-modal-subhead">Reassign who administers this church</h4>
          <form className="admin-modal-add" onSubmit={handleReassignAdmin}>
            <input
              type="email"
              value={newAdminEmail}
              onChange={(event) => setNewAdminEmail(event.target.value)}
              placeholder="New administering user email"
              required
            />
            <button type="submit" className="admin-modal-btn" disabled={busy}>Reassign</button>
          </form>
          <p className="admin-modal-hint">
            Every church must have an administering user, so this replaces rather than clears. They
            do not need to be a member.
          </p>
        </>
      )}
    </Modal>
  );
};

export const ChurchDeleteModal = ({ church, onClose, onDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      const result = await adminApi.deleteChurch(church.id);
      const { releasedMembers = 0, removedChallengeGoals = 0 } = result.data || {};
      onDeleted(`Deleted church "${church.name}", releasing ${releasedMembers} member(s) and removing ${removedChallengeGoals} challenge goal(s)`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this church.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      title={`Delete ${church.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            type="button"
            className="admin-modal-btn is-danger"
            onClick={handleDelete}
            disabled={confirmation !== church.name || deleting}
          >
            {deleting ? 'Deleting…' : 'Delete church'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        Members and readers keep their accounts and their whole reading history; they are just
        released from this church. Marking it inactive instead keeps everything and can be undone.
      </p>
      <p className="admin-modal-hint">
        <strong>Challenge goals belonging to this church are deleted with it</strong> and cannot be
        recovered. Everything else survives.
      </p>

      <Field label={`Type ${church.name} to confirm`}>
        <input
          type="text"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="off"
        />
      </Field>
    </Modal>
  );
};
